/**
 * Session wrapup tools — the closing ceremony for agent sessions.
 * Chain: GLO-47 (Wrapup), GLO-48 (Draft Harvest), ENT-33 (Session Wrapup Ceremony)
 *
 * session-wrapup: Reviews session drafts, quality coaches, suggests links, offers batch commit.
 * Aliases wrapup/finish were removed — "Also known as" is in the description for discoverability.
 *
 * Wrapup must run BEFORE session action=close (BR-8: active session required for writes).
 * Commit-on-confirm: wrapup surfaces drafts but only commits when explicitly asked.
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { z } from "zod/v3";
import { failureResult, successResult } from "../envelope.js";
import {
  getAgentSessionId,
  getApiKeyScope,
  getWorkspaceContext,
  kernelCall,
  kernelCallEnvelope,
  kernelQuery,
  kernelMutation,
  requireWriteAccess,
  recordSessionActivity,
} from "../client.js";
import { trackChainEntryCommitted, trackZeroCaptureAuditFired } from "../analytics.js";
import { getTopGaps, getSessionGaps } from "../gap-store.js";
import {
  detectTouchedRegistries,
  runCoherenceCheck,
  computeDelta,
  renderCoherenceDelta,
  findPersistentGaps,
  renderPersistentGapOffers,
  type CoherenceSnapshot,
  type CoherenceVerdict,
  type PersistentGap,
} from "../lib/coherence/index.js";
import { resolveProjectRoot } from "../lib/resolve-project-root.js";
import { composeWrapupView } from "../lib/compose-wrapup-view.js";

/**
 * `agentKnowledge.wrapupEnvelope` is a self-enveloping action — its OWN declared return
 * type is `Envelope<WrapupData | CommitAllData>` (confirmed: `convex/agentKnowledge/
 * wrapup.ts`'s handler signature), and `convex/http.ts`'s action dispatch (WP-321 E2,
 * ~line 1203-1224) passes an already-enveloped action's result through WITHOUT a second
 * outer wrap — the raw HTTP response body literally IS that envelope. `kernelCallEnvelope<T>`
 * extracts `.data` from the (unwrapped) HTTP JSON body and returns `{ok, summary, data: T}`,
 * so the value callers actually receive at `.data` is ONE LEVEL INSIDE the route's own
 * declared return type — the success branch's `data` payload, not the envelope itself.
 * `GatewayRouteReturnByName<Name>` alone therefore over-wraps by one layer for THIS call
 * shape; this extracts the true payload type instead of guessing at a hand-written mirror.
 *
 * Narrowed further to `WrapupData` specifically (not the full `WrapupData | CommitAllData`
 * union the route's server-side handler can also return for a different caller): BOTH of
 * this file's `kernelCallEnvelope("agentKnowledge.wrapupEnvelope", ...)` call sites pass
 * `action: "review"` literally (verified: lines below) — this file never sends `"commit-all"`
 * to this route at all (its own MCP-level `action === "commit-all"` branch commits via
 * individual `chain.commitEntry`/`chain.createEntryRelation(s)` calls instead, not this
 * route) — so `CommitAllData` is structurally impossible at either call site here.
 * `WrapupData` and `CommitAllData` share no clean discriminant field (both risk having a
 * `committed` key, with different types), so narrowing uses `drafts` — present only on
 * `WrapupData` — rather than a discriminant check.
 */
type WrapupEnvelopePayload = Extract<
  GatewayRouteReturnByName<"agentKnowledge.wrapupEnvelope">,
  { ok: true }
>["data"];
type WrapupEnvelopeData = Extract<WrapupEnvelopePayload, { drafts: unknown }>;

interface WrapupEntry {
  docId: string;
  entryId: string;
  name: string;
  collection: string;
  status: string;
  canonicalKey: string | null;
  wasCreated: boolean;
}

interface WrapupVerdict {
  passed: boolean;
  tier: string;
  // Matches the server's own `WrapupVerdict.weakest: unknown` declaration (convex/
  // agentKnowledge/wrapup.ts) exactly — real construction sites (convex/intelligence/
  // qualityCoaching.ts) confirm the runtime shape genuinely is `{criterion, hint} | null`,
  // but the server's own type is imprecise, so a client-side mirror re-declaring a
  // narrower type would silently diverge again. Cast to the known shape at the one
  // consumption site below instead (PR #395, Lane C S3b annotation pass).
  weakest: unknown;
  source: string;
}

interface LinkSuggestion {
  fromEntryId: string;
  toEntryId: string;
  type: string;
  label: string;
  // WP-474 E4-REWIRE: the graphSuggestLinks score (0-100), carried so wrapup-applied
  // auto-suggestions persist confidence instead of dropping it (matches smart-capture).
  confidence?: number;
}

/** Slim overflow element (TEN-2402) — NAMED beyond the review cap, sans per-entry enrichment. */
interface WrapupOverflowEntry {
  entryId: string;
  name: string;
  docId: string;
}

export interface WrapupData {
  drafts: WrapupEntry[];
  committed: WrapupEntry[];
  qualityVerdicts: Record<string, WrapupVerdict>;
  connectionCounts: Record<string, number>;
  summary: { created: number; modified: number; uncommitted: number; totalReviewed: number };
  overflow: number;
  /** TEN-2390/2402: still-draft entries beyond the cap (slim shape). Optional: a cross-deploy cache may predate it. */
  overflowDrafts?: WrapupOverflowEntry[];
  /** TEN-2402: committed entries beyond the cap, NAMED. Optional for the same reason. */
  overflowCommitted?: WrapupOverflowEntry[];
  /** TEN-2390: entries past the scan cap, never enumerated (invariant under commit). */
  overflowUnscanned?: number;
  wrapupCompletedAt: number | null;
  closeNudge: string;
  captureAudit: {
    message: string;
    suggestions: Array<{ collectionSlug: string; hint: string }>;
  } | null;
}

interface SuggestLinksResult {
  resolvedEntry: { entryId: string; name: string; docId: string };
  suggestions: {
    entryId: string;
    name: string;
    collectionSlug: string;
    score: number;
    recommendedRelationType: string;
    reasoning: string;
  }[];
}

interface WrapupReviewResult {
  text: string;
  data: WrapupData | null;
  suggestions: LinkSuggestion[];
  /** Set on early exits so the caller doesn't need to derive codes from text. */
  failureCode?: string;
  /** BET-89: number of knowledge gaps detected this session. */
  gapCount?: number;
  /** FEAT-184: coherence verdict when registry files were touched. */
  coherenceVerdict?: CoherenceVerdict;
  /** BET-101: persistent gaps eligible for Chain capture as tensions. */
  persistentGaps?: PersistentGap[];
  /** WP-321: kernel envelope fields surfaced for thin passthrough. */
  kernelSummary?: string;
  kernelNext?: import("../envelope.js").NextAction[];
  kernelMeta?: { durationMs?: number };
}

// ─── Concurrency helper ──────────────────────────────────────────────────────

async function mapWithConcurrency<T, U>(
  items: T[],
  mapper: (item: T) => Promise<U>,
  concurrency = 3,
): Promise<PromiseSettledResult<U>[]> {
  const results: PromiseSettledResult<U>[] = new Array(items.length);
  let index = 0;

  async function runNext(): Promise<void> {
    while (index < items.length) {
      const i = index++;
      try {
        const value = await mapper(items[i]);
        results[i] = { status: "fulfilled", value };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, () => runNext()),
  );
  return results;
}

// ─── Coherence snapshot retrieval (FEAT-184) ────────────────────────────────

interface SessionResponse {
  status: string;
  oriented: boolean;
  coherenceSnapshot?: {
    checkedAt: unknown;
    totalGaps: unknown;
    topGaps: unknown;
  };
}

function isValidTopGap(g: unknown): g is { registry: string; slug: string; severity: string } {
  if (typeof g !== "object" || g === null) return false;
  const obj = g as Record<string, unknown>;
  return (
    typeof obj.registry === "string" &&
    typeof obj.slug === "string" &&
    typeof obj.severity === "string" &&
    (obj.severity === "error" || obj.severity === "warning" || obj.severity === "info")
  );
}

async function fetchSessionCoherenceSnapshot(
  sessionId: string,
): Promise<CoherenceSnapshot | null> {
  try {
    const session = await kernelCall<GatewayRouteReturnByName<"agent.getSession">>("agent.getSession", {
      sessionId,
    });
    const raw = session?.coherenceSnapshot;
    if (
      raw &&
      typeof raw.checkedAt === "number" &&
      typeof raw.totalGaps === "number" &&
      Array.isArray(raw.topGaps) &&
      raw.topGaps.every(isValidTopGap)
    ) {
      return raw as unknown as CoherenceSnapshot;
    }
  } catch (err) {
    if (process.env.DEBUG_COHERENCE) {
      console.debug("[coherence] Snapshot fetch failed:", err instanceof Error ? err.message : err);
    }
  }
  return null;
}

// ─── Suggest links with concurrency ──────────────────────────────────────────

async function suggestLinksForEntries(entries: WrapupEntry[]): Promise<LinkSuggestion[]> {
  const suggestions: LinkSuggestion[] = [];

  const results = await mapWithConcurrency(
    entries,
    async (entry) => {
      const result = await kernelQuery<GatewayRouteReturnByName<"chain.graphSuggestLinks">>("chain.graphSuggestLinks", {
        entryId: entry.entryId,
        limit: 3,
      });
      return { entry, suggestions: result.suggestions ?? [] };
    },
    3,
  );

  for (const result of results) {
    if (result.status !== "fulfilled") continue;
    const { entry, suggestions: entrySuggestions } = result.value;
    // `entryId` is optional at the schema level (convex/schema.ts entries table) — a
    // suggestion candidate with no assigned human-readable ID isn't a usable link target,
    // so it's filtered out here rather than cast past (PR #395, Lane C S3b annotation pass).
    for (const s of entrySuggestions.filter((x): x is typeof x & { entryId: string } => x.entryId != null).slice(0, 2)) {
      suggestions.push({
        fromEntryId: entry.entryId,
        toEntryId: s.entryId,
        type: s.recommendedRelationType,
        label: `${entry.entryId} → ${s.entryId} (${s.name}) [${s.recommendedRelationType}]`,
        // WP-474 E4-REWIRE: carry the suggestion score forward (was dropped here).
        confidence: s.score,
      });
    }
  }

  return suggestions;
}

// ─── Core review logic ───────────────────────────────────────────────────────

async function runWrapupReview(): Promise<WrapupReviewResult> {
  const sessionId = getAgentSessionId();
  if (!sessionId) {
    return { text: "No active agent session. Call `session action=start` first.", data: null, suggestions: [], failureCode: "SESSION_REQUIRED" };
  }

  if (getApiKeyScope() === "read") {
    return { text: "Read-only session — nothing to review.", data: null, suggestions: [], failureCode: "READONLY_SCOPE" };
  }

  const { workspaceId } = await getWorkspaceContext();
  // WP-321 E3: delegate to kernel action; auth injected server-side via injectSessionAuth in http.ts
  const kernelEnvelope = await kernelCallEnvelope<WrapupEnvelopeData>("agentKnowledge.wrapupEnvelope", {
    workspaceId,
    sessionId,
    action: "review",
  });
  const data = kernelEnvelope.data;

  // TEN-2400: the ALREADY_COMPLETED short-circuit must be conditioned on the loop being
  // EMPTY, not merely on `wrapupCompletedAt` being set. A failed/partial batch (or drafts
  // captured after a clean wrapup) can leave `wrapupCompletedAt` set while drafts remain —
  // firing here would blind the surface forever. Loop-empty = no capped drafts, no overflow
  // drafts, nothing unscanned. (Distinct from the empty-session guard below.)
  const overflowDraftCount = (data.overflowDrafts ?? []).length;
  const overflowUnscanned = data.overflowUnscanned ?? 0;
  const loopEmpty = data.drafts.length === 0 && overflowDraftCount === 0 && overflowUnscanned === 0;
  if (data.wrapupCompletedAt && loopEmpty) {
    return { text: "Wrapup already completed this session.", data, suggestions: [], failureCode: "ALREADY_COMPLETED" };
  }

  if (
    data.drafts.length === 0 &&
    data.committed.length === 0 &&
    overflowDraftCount === 0 &&
    (data.overflowCommitted ?? []).length === 0
  ) {
    return { text: "Empty session — no entries to review.", data, suggestions: [], failureCode: "NOT_FOUND" };
  }

  const lines: string[] = [];

  lines.push(
    `**Session activity:** ${data.summary.created} created, ${data.summary.modified} modified, ` +
    `${data.summary.uncommitted} uncommitted drafts.`,
  );
  // TEN-2390: the truncation line is for entries the server could NOT enumerate at all
  // (past the scan cap) — NOT `overflow` (which now includes entries we DO name in the
  // beyond-cap group below). Using `overflow` would double-report named overflow entries.
  if (overflowUnscanned > 0) {
    lines.push(`_...and ${overflowUnscanned} more entr${overflowUnscanned === 1 ? "y" : "ies"} this session could not be enumerated (past the scan cap)._`);
  }
  lines.push("");

  if (data.drafts.length > 0) {
    lines.push("### Uncommitted Drafts", "");
    lines.push("| Entry | Collection | Quality | Connections | Coaching |");
    lines.push("|-------|------------|---------|-------------|----------|");

    for (const draft of data.drafts) {
      const verdict = data.qualityVerdicts[draft.entryId];
      const conns = data.connectionCounts[draft.entryId] ?? 0;
      const qualityStr = verdict
        ? `${verdict.passed ? "Pass" : "Needs work"} (${verdict.source})`
        : "—";
      // `weakest` is `unknown` server-side (imprecise type, real shape confirmed via
      // convex/intelligence/qualityCoaching.ts's construction sites); cast at use.
      const weakest = verdict?.weakest as { criterion: string; hint: string } | null | undefined;
      const coaching = weakest ? weakest.hint : "—";

      lines.push(`| \`${draft.entryId}\` ${draft.name} | ${draft.collection} | ${qualityStr} | ${conns} | ${coaching} |`);
    }
    lines.push("");
  }

  if (data.committed.length > 0) {
    lines.push(`### Already Committed (${data.committed.length})`, "");
    for (const entry of data.committed) {
      const conns = data.connectionCounts[entry.entryId] ?? 0;
      lines.push(`- \`${entry.entryId}\` [${entry.collection}] ${entry.name} (${conns} connections)`);
    }
    lines.push("");
  }

  // TEN-2402: entries beyond the review cap are NAMED here by `entryId — name` (the slim
  // shape carries no collection/quality enrichment — that is the cap window's job). Both
  // committed and still-draft overflow are listed so no entry past 15 is a bare count.
  const overflowDrafts = data.overflowDrafts ?? [];
  const overflowCommitted = data.overflowCommitted ?? [];
  if (overflowDrafts.length > 0 || overflowCommitted.length > 0) {
    lines.push(`### Beyond Review Cap (${overflowDrafts.length + overflowCommitted.length})`, "");
    lines.push("_Past the per-session enrichment cap — listed by id and name only._", "");
    for (const e of overflowCommitted) {
      lines.push(`- \`${e.entryId}\` — ${e.name} (committed)`);
    }
    for (const e of overflowDrafts) {
      lines.push(`- \`${e.entryId}\` — ${e.name} (draft)`);
    }
    lines.push("");
  }

  // Suggest links for low-connection entries (bounded concurrency)
  const lowConnEntries = [...data.drafts, ...data.committed].filter(
    (e) => (data.connectionCounts[e.entryId] ?? 0) < 2,
  );

  const suggestions = lowConnEntries.length > 0
    ? await suggestLinksForEntries(lowConnEntries.slice(0, 10))
    : [];

  if (suggestions.length > 0) {
    lines.push("### Suggested Links", "");
    for (const s of suggestions) {
      lines.push(`- ${s.label}`);
    }
    lines.push("");
  }

  const topGaps = getTopGaps(5);
  if (topGaps.length > 0) {
    lines.push("### Learning Opportunities", "");
    lines.push(`This session, agents looked for ${topGaps.length} topic${topGaps.length === 1 ? "" : "s"} not yet on the Chain:`, "");
    for (const gap of topGaps) {
      const freq = gap.count > 1 ? ` _(${gap.count}x)_` : "";
      lines.push(`- **${gap.query}** — not yet captured${freq}`);
    }
    lines.push("");
    lines.push("_Capturing these will make future sessions more effective._");
    lines.push("");
  }

  // ── FEAT-184 + BET-101: Coherence delta + persistent gap offers ─────────
  let coherenceVerdict: CoherenceVerdict | undefined;
  let persistentGaps: PersistentGap[] | undefined;
  try {
    const projectRoot = resolveProjectRoot();
    if (projectRoot) {
      const touchedFiles = detectTouchedRegistries(projectRoot);
      const sessionSnapshot = await fetchSessionCoherenceSnapshot(sessionId);
      const shouldCheck = touchedFiles.size > 0 || sessionSnapshot !== null;

      if (shouldCheck) {
        const outcome = await runCoherenceCheck(projectRoot);

        if (outcome.status === "skipped") {
          // TEN-2781 Design: VISIBLE fail-open — never a silent pass. The gateway was
          // unreachable, so drift was NOT evaluated this session; say so explicitly
          // rather than silently omitting the section. PR #405 review (Codex P2):
          // outcome.reason carries the CONCRETE failure (network error, or the
          // gateway's own code+message) instead of a one-size-fits-all message.
          lines.push("### Coherence Check");
          lines.push(`_Coherence check skipped — gateway unreachable. Registry drift not evaluated this session. (Reason: ${outcome.reason})_`);
          lines.push("");
        } else if (outcome.status === "ok") {
          const { report, snapshot: afterSnapshot } = outcome;

          if (sessionSnapshot) {
            const delta = computeDelta(sessionSnapshot, afterSnapshot);
            coherenceVerdict = delta.verdict;
            const deltaLines = renderCoherenceDelta(delta);
            lines.push(...deltaLines);

            if (touchedFiles.size > 0) {
              lines.push(
                `_This session touched ${touchedFiles.size} registry file${touchedFiles.size === 1 ? "" : "s"}: ` +
                `${[...touchedFiles].slice(0, 3).map((f) => `\`${f}\``).join(", ")}` +
                `${touchedFiles.size > 3 ? ` and ${touchedFiles.size - 3} more` : ""}._`,
              );
              lines.push("");
            }

            // BET-101: offer to capture persistent gaps as Chain tensions
            persistentGaps = findPersistentGaps(delta, report.violations);
            if (persistentGaps.length > 0) {
              lines.push(...renderPersistentGapOffers(persistentGaps));
            }
          } else if (touchedFiles.size > 0) {
            lines.push("### Coherence Check");
            lines.push(
              `This session touched ${touchedFiles.size} registry file${touchedFiles.size === 1 ? "" : "s"} ` +
              `but no session-start snapshot exists — showing absolute state.`,
            );
            const { summary } = report;
            if (summary.total === 0) {
              lines.push("All registries aligned — no gaps.");
            } else {
              lines.push(`**${summary.total} gap${summary.total === 1 ? "" : "s"}**: ${summary.errors} error${summary.errors === 1 ? "" : "s"}, ${summary.warnings} warning${summary.warnings === 1 ? "" : "s"}, ${summary.infos} info.`);
            }
            lines.push("");
          }
        }
        // outcome.status === "no-data": too few registries resolved locally — nothing
        // to check, no section rendered (unchanged pre-Lane-D behavior).
      }
    }
  } catch (err) {
    if (process.env.DEBUG_COHERENCE) {
      console.debug("[coherence] Wrapup coherence check failed:", err instanceof Error ? err.message : err);
    }
  }

  // TEN-2402: the actionable draft set is the FULL set (capped ∪ overflow), so the agent
  // sees every promotable id, not just the first 15.
  const allDraftEntries = [...data.drafts, ...overflowDrafts];
  if (allDraftEntries.length > 0) {
    const draftIds = allDraftEntries.map((d) => `\`${d.entryId}\``).join(", ");
    lines.push("### Actions", "");
    lines.push(`Uncommitted: ${draftIds}`);
    lines.push("- **Accept all:** call \`session action=wrapup-commit\`");
    lines.push("- **Skip:** call \`session action=close\` — drafts remain for next session's orient recovery.");
  }
  if (overflowUnscanned > 0) {
    lines.push("");
    lines.push(
      `> _${overflowUnscanned} draft(s) beyond the scan cap can't be auto-committed here — ` +
      "commit them individually via `entries action=commit`, or close via `session action=close`._",
    );
  }

  // WP-306 S2: Render captureAudit (zero-capture sessions with activity) and closeNudge.
  if (data.captureAudit) {
    lines.push("");
    lines.push("### Capture Audit");
    lines.push("");
    lines.push(`_${data.captureAudit.message}_`);
    if (data.captureAudit.suggestions.length > 0) {
      lines.push("");
      lines.push("**What to consider capturing:**");
      for (const s of data.captureAudit.suggestions) {
        lines.push(`- **${s.collectionSlug}**: ${s.hint}`);
      }
    }
    lines.push("");
  }

  lines.push("");
  lines.push(`> **${data.closeNudge}**`);

  // WP-306 S3: Emit zero_capture_audit_fired event (STD-155)
  if (data.captureAudit) {
    const wsCtx = await getWorkspaceContext();
    if (wsCtx) {
      trackZeroCaptureAuditFired(wsCtx.workspaceId, {
        suggestion_count: data.captureAudit.suggestions.length,
      });
    }
  }

  const gapCount = getSessionGaps().length;
  return {
    text: lines.join("\n"),
    data,
    suggestions,
    gapCount,
    coherenceVerdict,
    persistentGaps,
    kernelSummary: kernelEnvelope.summary,
    kernelNext: kernelEnvelope.next,
    kernelMeta: kernelEnvelope._meta,
  };
}

// ─── Commit flow ─────────────────────────────────────────────────────────────

async function runWrapupCommitAll(
  data: WrapupData,
  cachedSuggestions: LinkSuggestion[],
): Promise<{ text: string; committed: number; proposalsCreated: number; linksCreated: number; linksFailed: number; failed: number; overflowUnscanned: number }> {
  requireWriteAccess();

  const sessionId = getAgentSessionId();
  if (!sessionId) {
    return { text: "No active session.", committed: 0, proposalsCreated: 0, linksCreated: 0, linksFailed: 0, failed: 0, overflowUnscanned: 0 };
  }

  const wsCtx = await getWorkspaceContext();

  const results: { entryId: string; ok: boolean; proposed?: boolean; error?: string }[] = [];
  let linksCreated = 0;
  let linksFailed = 0;
  // WP-474 E5: auto-links the judge advised but did NOT write (untypeable) — surfaced, never silently dropped.
  let linksAdvised = 0;
  const linkFailureDetails: string[] = [];
  let proposalsCreated = 0;

  // TEN-2402: commit the FULL promotable set = capped drafts ∪ overflow drafts. The `?? []`
  // coalesce keeps a cross-deploy cache object that predates `overflowDrafts` safe. Each
  // overflow element carries `docId` (S1) so recordSessionActivity below never throws on
  // an undefined docId.
  const allDrafts = [...data.drafts, ...(data.overflowDrafts ?? [])];
  const overflowUnscanned = data.overflowUnscanned ?? 0;

  for (const draft of allDrafts) {
    try {
      const result = await kernelMutation<GatewayRouteReturnByName<"chain.commitEntry">>("chain.commitEntry", {
        entryId: draft.entryId,
        author: `agent:${sessionId}`,
        sessionId,
      });
      // WP-465 slice ⑤ (Option B): a refused accept RETURNS a structured payload — record it
      // as a non-committed draft so wrapup never counts/latches it as accepted (it persists).
      // `chain.commitEntry`'s real return is a discriminated union whose plain-success branch
      // has NO `status` field at all (not even `status: undefined`) — `'status' in result`
      // narrows safely before reading it (PR #395, Lane C S3b annotation pass).
      if (result && "status" in result && result.status === "coherency_refused") {
        const refusal = "refusal" in result ? result.refusal : undefined;
        results.push({
          entryId: draft.entryId,
          ok: false,
          error: `coherency gate refused this accept — ${(refusal as { reason?: string } | undefined)?.reason ?? "refused by the coherency gate"}`,
        });
        continue;
      }
      const proposed = !!(result && "status" in result && result.status === "proposal_created");
      if (!proposed) {
        await recordSessionActivity({ entryModified: draft.docId });
        trackChainEntryCommitted(wsCtx.workspaceId, {
          entry_id: draft.entryId,
          commit_method: "manual",
          surface: "mcp_wrapup",
        });
      }
      if (proposed) {
        proposalsCreated++;
      }
      results.push({ entryId: draft.entryId, ok: true, proposed });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      results.push({ entryId: draft.entryId, ok: false, error: msg });
    }
  }

  // Use cached suggestions from review phase, or fetch fresh if none
  const suggestionsToApply = cachedSuggestions.length > 0
    ? cachedSuggestions
    : await suggestLinksForEntries(
        [...data.drafts, ...data.committed].filter(
          (e) => (data.connectionCounts[e.entryId] ?? 0) < 2,
        ).slice(0, 10),
      );
  const linkableSourceIds = new Set([
    ...data.committed.map((entry) => entry.entryId),
    ...results.filter((r) => r.ok && !r.proposed).map((r) => r.entryId),
  ]);

  const toApply = suggestionsToApply
    .filter((s) => linkableSourceIds.has(s.fromEntryId))
    .slice(0, 15);
  const governsRels = toApply.filter((s) => s.type === "governs");
  const batchRels = toApply.filter((s) => s.type !== "governs");

  if (batchRels.length > 0) {
    try {
      const batchResult = await kernelMutation<GatewayRouteReturnByName<"chain.createEntryRelations">>("chain.createEntryRelations", {
        // WP-474 E4-REWIRE: wrapup-applied suggestions are auto-inferred (no one chose the
        // type) → mark "auto-link" + carry confidence (DEC-1399), matching smart-capture.
        relations: batchRels.map((s) => ({
          fromEntryId: s.fromEntryId,
          toEntryId: s.toEntryId,
          type: s.type,
          proposedBy: "auto-link",
          ...(s.confidence !== undefined ? { confidence: s.confidence } : {}),
        })),
        sessionId,
      });
      linksCreated += batchResult?.created ?? 0;
      // WP-474 E5: the judge may advise (not write) untypeable auto-link edges — those are
      // excluded from `failed` server-side (status:'advised'), so tally them separately.
      linksAdvised += batchResult?.advised ?? 0;

      // Surface per-relation failures so the user knows which links weren't created
      if (batchResult && batchResult.failed > 0) {
        linksFailed += batchResult.failed;
        const failedResults = batchResult.results
          .map((r, i) => ({ ...r, index: i }))
          .filter((r) => !r.ok && r.status !== 'advised');
        for (const fr of failedResults) {
          const rel = batchRels[fr.index];
          if (rel) {
            linkFailureDetails.push(
              `${rel.fromEntryId} → ${rel.toEntryId} [${rel.type}]: ${fr.error ?? "unknown error"}`,
            );
          }
        }
      }
    } catch (err) {
      // Total batch failure — count all as failed
      linksFailed += batchRels.length;
      const msg = err instanceof Error ? err.message : String(err);
      linkFailureDetails.push(`Batch creation failed (${batchRels.length} relations): ${msg}`);
    }
  }

  for (const s of governsRels) {
    try {
      const result = await kernelMutation<GatewayRouteReturnByName<"chain.createEntryRelation">>("chain.createEntryRelation", {
        fromEntryId: s.fromEntryId,
        toEntryId: s.toEntryId,
        type: s.type,
        // WP-474 E4-REWIRE: wrapup-applied governs suggestion is auto-inferred → "auto-link".
        proposedBy: "auto-link",
        ...(s.confidence !== undefined ? { confidence: s.confidence } : {}),
        sessionId,
      });
      // WP-474 E5: the judge may advise (not write) an untypeable auto-link edge — that is
      // neither a proposal nor a created link, so it must not inflate linksCreated. Tally it as
      // advised (same as the batch path) so the advised-not-written message surfaces it rather
      // than the governs edge silently disappearing from the wrapup result.
      if (result?.status === "proposal_created") {
        // Consent proposal (governs requires async sign-off) — not a written link; left
        // uncounted here as before (pre-existing behavior, unchanged by this fix).
      } else if (result?.advised) {
        linksAdvised++;
      } else {
        linksCreated++;
      }
    } catch (err) {
      linksFailed++;
      const msg = err instanceof Error ? err.message : String(err);
      linkFailureDetails.push(`${s.fromEntryId} → ${s.toEntryId} [${s.type}]: ${msg}`);
    }
  }

  const committed = results.filter((r) => r.ok && !r.proposed).length;
  const failed = results.filter((r) => !r.ok);

  // TEN-2400: latch the wrapup (writes `wrapupCompletedAt` server-side) ONLY on a clean
  // drain — no failed commit AND nothing left unscanned. Latching on a failed/partial batch
  // would set `wrapupCompletedAt` and (combined with the review short-circuit) blind the
  // surface to the still-stranded drafts. A failed batch records no wrapup metrics, which is
  // intentional and consistent with the kernel envelope path.
  const cleanDrain = failed.length === 0 && overflowUnscanned === 0;
  if (cleanDrain) {
    await kernelCall<GatewayRouteReturnByName<"agent.recordWrapup">>("agent.recordWrapup", {
      sessionId,
      draftsReviewed: allDrafts.length,
      draftsCommitted: committed,
      linksCreated,
    });
  }

  const lines: string[] = [
    "## Wrapup Complete",
    "",
    `**${committed}** drafts accepted, **${proposalsCreated}** proposals created, **${linksCreated}** links created.`,
  ];

  if (linksFailed > 0) {
    lines.push(`**${linksFailed}** link${linksFailed === 1 ? "" : "s"} failed to create.`);
  }

  // WP-474 E5: surface the advised-not-written cohort unconditionally — never gated on failures,
  // since an advised edge is neither a success nor a failure.
  if (linksAdvised > 0) {
    lines.push(`**${linksAdvised}** auto-link(s) advised, not written (untypeable) — accept with \`relations action=create\` to promote.`);
  }

  if (failed.length > 0) {
    lines.push("");
    lines.push("### Failed to accept");
    for (const f of failed) {
      lines.push(`- \`${f.entryId}\`: ${f.error}`);
    }
    lines.push("_These remain as drafts for next session._");
  }

  if (linkFailureDetails.length > 0) {
    lines.push("");
    lines.push("### Failed to create links");
    for (const detail of linkFailureDetails) {
      lines.push(`- ${detail}`);
    }
    lines.push("_You can create these links manually with `entries action=create-relation`._");
  }

  return {
    text: lines.join("\n"),
    committed,
    proposalsCreated,
    linksCreated,
    linksFailed,
    failed: failed.length,
    overflowUnscanned,
  };
}

// ─── Shared schema (still used by session.ts's discriminated variants + tests) ──

export const wrapupSchema = z.object({
  action: z.enum(["review", "commit-all"]).optional().describe(
    "Action to perform. 'review' (default) shows the wrapup summary. 'commit-all' accepts all uncommitted drafts and creates suggested links.",
  ),
});

/**
 * Factory for the wrapup handler (WP-484 S2, §3) — reused by the legacy
 * `session-wrapup` tool and the new `session action=wrapup-review` /
 * `session action=wrapup-commit`. One business-logic path (this function
 * body), but each registration gets its OWN review cache — a shared module
 * singleton would leak cached review state across unrelated tests AND across
 * unrelated tool registrations in the same process. The former
 * `registerWrapupTools`-closure-local cache is preserved exactly, just
 * factored out so a second caller (session.ts) can mint its own instance.
 */
export function createWrapupHandler() {
  let lastReviewData: WrapupData | null = null;
  let lastReviewSuggestions: LinkSuggestion[] = [];
  let lastReviewSessionId: string | null = null;

  return async ({ action }: { action?: "review" | "commit-all" }) => {
    if (action === "commit-all") {
        const sessionId = getAgentSessionId();
        if (!sessionId) {
          return failureResult(
            "No active agent session.",
            "SESSION_REQUIRED",
            "No active agent session.",
            "Start a session first.",
            [{ tool: "session", description: "Start a session", parameters: { action: "start" } }],
          );
        }

        const useCachedReview = lastReviewSessionId === sessionId;
        let data: WrapupData;
        if (useCachedReview && lastReviewData) {
          data = lastReviewData;
        } else {
          const { workspaceId } = await getWorkspaceContext();
          // WP-321 E3: delegate to kernel action; auth injected server-side via injectSessionAuth in http.ts
          data = (await kernelCallEnvelope<WrapupEnvelopeData>("agentKnowledge.wrapupEnvelope", {
            workspaceId,
            sessionId,
            action: "review",
          })).data;
        }
        // TEN-2402: the early-exit guard must consider the FULL promotable set. Guarding on
        // `data.drafts.length === 0` alone dead-codes the overflow iteration for an
        // all-overflow-draft session (first 15 committed, drafts only past the cap).
        if (data.drafts.length === 0 && (data.overflowDrafts ?? []).length === 0) {
          return failureResult("No uncommitted drafts to accept.", "NOT_FOUND", "No uncommitted drafts to accept.", "Nothing to accept.");
        }

        const result = await runWrapupCommitAll(data, useCachedReview ? lastReviewSuggestions : []);
        lastReviewData = null;
        lastReviewSuggestions = [];
        lastReviewSessionId = null;
        const linkWarnSuffix = result.linksFailed > 0 ? `, ${result.linksFailed} link(s) failed` : "";
        // TEN-2400: derive `next` from the commit results — never a static close.
        //  - overflowUnscanned > 0 → terminal (committing can't shrink it).
        //  - any failed commit → re-review/fix.
        //  - clean drain → close.
        const commitNext =
          result.overflowUnscanned > 0
            ? [
                {
                  tool: "session",
                  description:
                    `${result.overflowUnscanned} draft(s) beyond the scan cap can't be auto-committed here — ` +
                    "commit them individually via `entries action=commit`, or close via `session action=close`.",
                  parameters: { action: "close" },
                },
              ]
            : result.failed > 0
              ? [{ tool: "session", description: "Re-review remaining drafts before closing", parameters: { action: "wrapup-review" } }]
              : [{ tool: "session", description: "Close the session now that drafts are committed", parameters: { action: "close" } }];
        return successResult(
          result.text,
          `Wrapup processed: ${result.committed} accepted, ${result.proposalsCreated} proposals, ${result.linksCreated} links${linkWarnSuffix}.`,
          {
            committed: result.committed,
            proposalsCreated: result.proposalsCreated,
            linksCreated: result.linksCreated,
            linksFailed: result.linksFailed,
            failed: result.failed,
          },
          commitNext,
        );
      }

      const { text, data, suggestions, failureCode, gapCount, coherenceVerdict, persistentGaps, kernelSummary, kernelNext, kernelMeta } = await runWrapupReview();
      lastReviewData = data;
      lastReviewSuggestions = suggestions;
      lastReviewSessionId = getAgentSessionId();

      // TEN-2402: composeWrapupView merges capped ∪ overflow; the parity test pins this
      // helper against the CLI twin. Use it for the empty-session check and the local next
      // fallback so both honor the full set, not just the capped 15.
      const composed = data ? composeWrapupView(data) : null;
      const fullText = composed && (composed.drafts.length > 0 || composed.committed.length > 0)
        ? `## Session Wrapup\n\n${text}`
        : text;

      if (failureCode) {
        return failureResult(fullText, failureCode, text);
      }

      // TEN-2390/2400: local fallback next must derive from the merged remaining set + the
      // unscanned count, or it silently keeps the old capped-only logic when kernelNext is
      // absent. `canClose` already AND-s drafts-empty + unscanned-zero.
      const localNext =
        composed && !composed.canClose
          ? [{ tool: "session", description: "Accept all drafts", parameters: { action: "wrapup-commit" } }]
          : undefined;
      const next = kernelNext || localNext;

      const gapsSummary = gapCount ? `, ${gapCount} knowledge gaps detected` : "";
      const coherenceSummary = coherenceVerdict ? `, coherence: ${coherenceVerdict}` : "";
      const persistentSummary = persistentGaps?.length ? `, ${persistentGaps.length} persistent coherence gaps` : "";
      // TEN-2402: report the MERGED (capped ∪ overflow) counts, not the capped-only `data`
      // lists. The rendered text already names the full set (allDraftEntries / Beyond Review
      // Cap), so capped-only numbers here would make structuredContent under-report drafts a
      // machine caller can see listed above — the exact "surface lies about state" defect.
      const draftsCount = composed?.drafts.length ?? data!.drafts.length;
      const committedCount = composed?.committed.length ?? data!.committed.length;
      const summaryText = `Session review: ${draftsCount} uncommitted, ${committedCount} committed, ${suggestions.length} link suggestions${gapsSummary}${coherenceSummary}${persistentSummary}.`;
      return {
        content: [{ type: "text" as const, text: fullText }],
        structuredContent: {
          ok: true as const,
          summary: kernelSummary || summaryText,
          data: {
            drafts: draftsCount,
            committed: committedCount,
            uncommitted: data!.summary.uncommitted,
            suggestedLinks: suggestions.length,
            knowledgeGaps: gapCount ?? 0,
            ...(coherenceVerdict ? { coherenceVerdict } : {}),
            ...(persistentGaps?.length ? { persistentCoherenceGaps: persistentGaps.length } : {}),
          },
          next,
          _meta: kernelMeta,
        },
      };
  };
}

// WP-484 S3 (clean break, TEN-2157): the standalone `session-wrapup` tool
// retired here. createWrapupHandler / runWrapupReview stay live — session.ts
// (the `session` compound, action=wrapup-review / action=wrapup-commit) is
// their only remaining caller.

export { runWrapupReview, type WrapupReviewResult };
